// ─────────────────────────────────────────────────────────────────────────────
// src/hooks/prefetchSync.ts
// Pure DTO→local sync logic for the upcoming-appointments prefetch. Split out
// from useUpcomingPrefetch.ts (which owns the Web Worker/import.meta wiring)
// so this logic is unit-testable under Jest/CRA.
// ─────────────────────────────────────────────────────────────────────────────
import { upsertPatient } from './repos/patientRepo';
import { getSedationById, createSedation, updateSedation, type SedationDetail } from './repos/sedationDetailRepo';
import type { UpcomingPatientDto, UpcomingAppointmentDto, SedationDetailDto, ApiClient } from '../api/generated';
import type { Patient } from '../types';
import { convertSedationDetailDtoToLocal } from './convertSedationDetailDto';
import { dateOnlyISO } from '../utilis/dateTime';
import { fieldsFromDto } from '../utils/medicalHistoryDraft';

// ─── DTO → local entity mappers ───────────────────────────────────────────────

function mapPatient(dto: UpcomingPatientDto): Partial<Patient> {
  return {
    Id: dto.id,
    Title: dto.title || '',
    FirstName: dto.firstName || '',
    LastName: dto.lastName || '',
    DateOfBirth: dateOnlyISO(dto.dateOfBirth) || null,
    Age: dto.age || 0,
    Address: dto.address || null,
    Town: dto.town || null,
    Country: dto.country || null,
    PostCode: dto.postCode || null,
    PhoneNumber: dto.phoneNumber || null,
    AlternativePhoneNumber: dto.alternativePhoneNumber || null,
    Email: dto.email || null,
    MedicalHistoryFields: fieldsFromDto(dto.medicalHistoryFields),
    MedicalHistory: dto.medicalHistory || null,
    Medications: dto.medications || null,
    Allergies: dto.allergies || null,
    AnestheticHistory: dto.anestheticHistory || null,
    ASAClassification: dto.asaClassification || 0,
    SmokingStatus: dto.smokingStatus || 'None',
    AlcoholStatus: dto.alcoholStatus || 'None',
    AirwayDifficultyAnticipated: (dto as any).airwayDifficultyAnticipated ?? null,
    AirwayNote: (dto as any).airwayNote ?? null,
    RecreationalDrugUse: (dto as any).recreationalDrugUse ?? null,
    RecreationalDrugNote: (dto as any).recreationalDrugNote ?? null,
    Vapes: (dto as any).vapes ?? null,
    Height: dto.height || null,
    HeightFormat: dto.heightFormat || 'cm',
    Weight: dto.weight || null,
    WeightFormat: dto.weightFormat || 'kg',
    BMI: dto.bmi || 0,
    TicketId: dto.ticketId || null,
    ModifiedDateTime: dto.modifiedDateTime || null,
  };
}

// Fields captured live during a sedation session — a prefetch tick must never
// touch these, only the sedation workflow itself does. Everything else on
// SedationDetail is administrative/reference data set ahead of the visit
// (reason, procedure, surgeon, key points, IOSN/MDAS score, ...) and should
// always flow through from the API, or it silently goes stale locally — this
// is exactly the bug that previously left Reason/MDAS unpopulated.
const LIVE_SESSION_KEYS = new Set<keyof SedationDetail>([
  'Id', 'CreatedDateTime', 'Synced', 'Deactivated', 'ModifiedDateTime', 'PendingSync', 'LastSyncedAt',
  'Status', 'BloodPressure', 'PulseRate', 'CO2EndTidal', 'ECG', 'SAO2', 'RespiratoryRate',
  'ReplayCount', 'BIS', 'CapnographyAttached', 'BISAttached', 'BlanketOverPatient',
  'Cannulation', 'ConsentSigned', 'PdfUrl', 'PatientPackUrl', 'FileType',
]);

// ─── Selective sedation upsert ────────────────────────────────────────────────
// If PendingSync is set, the record is in-progress or locally edited — skip.

export async function upsertPrefetchedSedation(dto: SedationDetailDto, patientId: string) {
  if (!dto.id) return;
  const converted = convertSedationDetailDtoToLocal(dto, patientId);
  const existing = await getSedationById(dto.id);
  if (!existing) {
    await createSedation(converted as any);
    return;
  }
  if (existing.PendingSync) return;

  const merged: SedationDetail = { ...existing };
  (Object.keys(converted) as (keyof SedationDetail)[]).forEach((key) => {
    if (LIVE_SESSION_KEYS.has(key)) return;
    const incomingValue = converted[key];
    // Never let a missing/blank field in this payload erase data already saved locally.
    if (incomingValue !== undefined && incomingValue !== null && incomingValue !== '') {
      (merged as any)[key] = incomingValue;
    }
  });

  if (JSON.stringify(merged) !== JSON.stringify(existing)) {
    await updateSedation(dto.id, merged);
  }
}

// ─── Batch processor ──────────────────────────────────────────────────────────

export async function processAppointments(appointments: UpcomingAppointmentDto[]) {
  for (const apt of appointments) {
    if (!apt.patient?.id) continue;
    await upsertPatient(mapPatient(apt.patient));
    if (apt.sedationDetail) {
      await upsertPrefetchedSedation(apt.sedationDetail, apt.patient.id);
    }
  }
}

// ─── Standalone function for manual per-patient refresh (used by Patients.tsx) ─

export async function prefetchForPatient(client: ApiClient, patientId: string): Promise<number> {
  const response = await client.schedule.getApiScheduleUpcomingPrefetch(undefined, undefined, patientId);
  const appointments = response?.data?.appointments ?? [];
  await processAppointments(appointments);
  return appointments.length;
}
